import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatDistanceToNow } from "date-fns";
import { useDashboardNotifications } from "../../context/DashboardNotificationsContext";
import {
  getCategoryGradient,
  getCategoryMeta,
  getCategoryWash,
  isCriticalPriority,
} from "../../data/DashboardNotifications/notificationStyles";

const BODY_TRUNCATE_LENGTH = 120;

const NotificationCard = ({ message }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { dismiss } = useDashboardNotifications();
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(true);

  const meta = getCategoryMeta(message.category);
  const critical = isCriticalPriority(message.priority);
  const isLong = message.body?.length > BODY_TRUNCATE_LENGTH;
  const displayBody =
    !isLong || expanded
      ? message.body
      : `${message.body.slice(0, BODY_TRUNCATE_LENGTH)}…`;

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => dismiss(message.id), 200);
  };

  return (
    <Collapse in={visible} timeout={200}>
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          p: 1.5,
          mb: 1.25,
          borderRadius: "12px",
          background: getCategoryWash(meta, isDark),
          boxShadow: critical
            ? `0 0 0 1.5px ${meta.accent}, 0 4px 14px ${meta.accent}40`
            : `0 2px 8px ${meta.accent}22`,
          transition: "transform 0.18s ease, box-shadow 0.18s ease",
          "&:hover": {
            transform: reduceMotion ? "none" : "translateY(-2px)",
            boxShadow: `0 6px 18px ${meta.accent}44`,
          },
          ...(reduceMotion
            ? {}
            : {
                animation: "dashboardCardFadeIn 0.3s ease-out",
                "@keyframes dashboardCardFadeIn": {
                  from: { opacity: 0, transform: "translateY(6px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }),
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: getCategoryGradient(meta, isDark),
            boxShadow: `0 2px 8px ${meta.accent}55`,
            ...(critical && !reduceMotion
              ? {
                  animation: "dashboardCardPulseRing 1.6s ease-in-out infinite",
                  "@keyframes dashboardCardPulseRing": {
                    "0%, 100%": { boxShadow: `0 0 0 0 ${meta.accent}88` },
                    "50%": { boxShadow: `0 0 0 5px ${meta.accent}00` },
                  },
                }
              : {}),
          }}
        >
          <meta.Icon sx={{ color: "#fff", fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, flex: 1, color: isDark ? "#f1f5f9" : "#1e293b" }}
            >
              {message.title}
            </Typography>
            {message.isDismissible && (
              <IconButton
                size="small"
                aria-label={`Dismiss notification: ${message.title}`}
                onClick={handleDismiss}
                sx={{ p: 0.25 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{ color: isDark ? "rgba(255,255,255,0.75)" : "#475569", mt: 0.25 }}
          >
            {displayBody}
            {isLong && (
              <Link
                component="button"
                type="button"
                onClick={() => setExpanded((e) => !e)}
                sx={{ ml: 0.5, fontSize: "0.8rem", fontWeight: 600, color: meta.accent }}
              >
                {expanded ? "Show less" : "Show more"}
              </Link>
            )}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.75 }}>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? "rgba(255,255,255,0.5)" : "#94a3b8",
                fontWeight: 600,
              }}
            >
              {message.createdAt
                ? formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })
                : ""}
            </Typography>
            {message.ctaLabel && message.ctaUrl && (
              <Button
                size="small"
                variant="text"
                href={message.ctaUrl}
                sx={{
                  textTransform: "none",
                  color: meta.accent,
                  fontWeight: 700,
                  minWidth: 0,
                  p: 0,
                }}
              >
                {message.ctaLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
};

export default NotificationCard;
